import {
  Breadcrumb,
  Breadcrumbs,
  Autocomplete,
  Icons,
  Skeleton,
  ListItem,
  EllipsisWithTooltip,
  Typography,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';
import { Cluster, Node } from '@cast/types';

import { Link } from 'components/router';

import { makeNodeListLink } from '../../_utils/links';

type Props = {
  cluster: Cluster;
  currentNode?: Node;
  nodes?: Node[];
};

const NodesDropdown = ({
  nodes,
  currentNode,
}: {
  nodes: Node[];
  currentNode: Node;
}) => {
  return (
    <Autocomplete
      open
      sx={{ display: 'inline', textDecoration: 'inherit' }}
      virtualization
      options={nodes}
      value={currentNode.id}
      optionValue={(node) => node.id}
      testId="node-details-breadcrumbs-dropdown"
      listSx={{
        width: 480,
        maxHeight: 380,
      }}
      searchFilter={(node, value) => {
        return (node.name || '').includes(value);
      }}
      renderOption={(node, itemProps, selected) => {
        return (
          <ListItem
            {...itemProps}
            selected={selected}
            sx={
              selected
                ? {
                    backgroundColor: colorOrToken(
                      'blue.50',
                      tokens.theme.bg.selection.primarySubtle
                    ),
                    color: colorOrToken(
                      'blue.700',
                      tokens.theme.fg.selection.onPriamrySubtle
                    ),
                  }
                : undefined
            }
          >
            <Stack
              component={Link}
              // Replacing the current node id with the new node id in order to keep the same tab active
              to={location.pathname.replace(currentNode.id, node.id)}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={4}
              sx={{
                textDecoration: 'none',
              }}
            >
              <EllipsisWithTooltip
                TypographyProps={{
                  variant: 'P14R',
                  color: selected
                    ? 'inherit'
                    : colorOrToken(
                        'grey.900',
                        tokens.theme.fg.structural.primary
                      ),
                  noWrap: true,
                  sx: {
                    width: '400px',
                  },
                }}
              >
                {node.name}
              </EllipsisWithTooltip>
            </Stack>
          </ListItem>
        );
      }}
      trigger={
        <Stack
          display="inline-flex"
          direction="row"
          alignItems="center"
          gap={4}
          sx={{
            textDecoration: 'inherit',
            color: 'inherit',
          }}
          data-testid="node-details-breadcrumbs-dropdown-trigger"
        >
          <Typography display="inline" variant="P12M" color="inherit">
            {currentNode.name}
          </Typography>
          <Icons.CaretDown size={16} weight="bold" color="currentColor" />
        </Stack>
      }
    />
  );
};

const CurrentNodeBreadcrumb = ({
  currentNode,
  nodes,
}: {
  currentNode: Node;
  nodes?: Node[];
}) => {
  return (
    <Breadcrumb data-testid="node-details-breadcrumbs-current-node-breadcrumb">
      Node:{' '}
      {nodes?.length ? (
        <NodesDropdown nodes={nodes} currentNode={currentNode} />
      ) : (
        currentNode.name
      )}
    </Breadcrumb>
  );
};

export const NodeDetailsBreadcrumbs = ({
  currentNode,
  cluster,
  nodes,
}: Props) => {
  return (
    <Breadcrumbs data-testid="node-details-breadcrumbs">
      <Breadcrumb data-testid="node-details-breadcrumbs-node-list-link">
        <Link to={makeNodeListLink(cluster.id)}>Node list</Link>
      </Breadcrumb>
      {currentNode ? (
        <CurrentNodeBreadcrumb currentNode={currentNode} nodes={nodes} />
      ) : (
        <Skeleton variant="text" width={300} height={18} />
      )}
    </Breadcrumbs>
  );
};
